import { memo, useCallback, useId, useState } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type WorkspaceAgentDefinitionField } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/data/types';
import { WorkspaceAgentDefinitionRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/data/WorkspaceAgentDefinitionRow';

export type WorkspaceAgentDefinitionListProps = {
  'data-testid'?: string;
  fields: WorkspaceAgentDefinitionField[];
  /** Withheld for an unlabelled list, which is how a settings recap reads beneath its own result. */
  heading?: string;
  /**
   * Withheld to show every field. Supplied to cut a long list to its first few, because the
   * interesting rows are almost always at the top and an uncut list of twenty pushes the rest of the
   * answer out of view.
   */
  previewCount?: number;
};

/** Label/value pairs — the metadata the agent read, or the settings a run will use. */
export const WorkspaceAgentDefinitionList = memo(
  ({ 'data-testid': testId, fields, heading, previewCount }: WorkspaceAgentDefinitionListProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const fieldsId = useId();

    const handleToggle = useCallback(() => setIsExpanded((value) => !value), []);

    const isTruncated = previewCount !== undefined && fields.length > previewCount;
    const visibleFields = isTruncated && !isExpanded ? fields.slice(0, previewCount) : fields;

    return (
      <div data-testid={testId}>
        {heading ? <p className="font-medium ads:text-primary text-12 text-gray-12 leading-4">{heading}</p> : null}
        <dl className={cn('space-y-1.5', heading && 'pt-1.5')} id={fieldsId}>
          {visibleFields.map((field) => (
            <WorkspaceAgentDefinitionRow key={field.label} label={field.label} value={field.value} />
          ))}
        </dl>
        {isTruncated ? (
          <button
            // The list it lengthens, rather than a panel of its own: the rows above stay put and the
            // toggle appends to them, which is why this is not a `WorkspaceAgentDisclosure`.
            aria-controls={fieldsId}
            aria-expanded={isExpanded}
            className="mt-1 inline-flex items-center gap-0.5 py-1 font-medium ads:text-metadata text-12 text-gray-10 leading-4 ads:hover:text-primary hover:text-gray-12 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8"
            onClick={handleToggle}
            type="button"
          >
            {isExpanded ? 'Show less' : `Show ${fields.length - previewCount} more`}
            <ChevronDownIcon className={cn('transition-transform', isExpanded && 'rotate-180')} size="sm" />
          </button>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentDefinitionList.displayName = 'WorkspaceAgentDefinitionList';
